import { AnyAction } from 'redux';
import { ErrorData } from '@synergy/api';
import { ErrorMessage } from '../../../../../../common/models/enums/error-message';
import { FormikErrors } from 'formik';
import { LocalizedString } from '@synergy/models';
import lodashSet from 'lodash/set';

import {
    PRODUCT_DETAIL_ACTIVATE,
    PRODUCT_DETAIL_DEACTIVATE,
    PRODUCT_DETAIL_RESET,
} from '../../store/product-detail-actions';
import {
    PRODUCT_DETAIL_OVERVIEW_ERROR,
    PRODUCT_DETAIL_OVERVIEW_FETCH,
    PRODUCT_DETAIL_OVERVIEW_RECEIVE,
    PRODUCT_DETAIL_OVERVIEW_UPDATE,
} from './product-detail-overview-actions';
import { PackingUnit } from '../models/packing-unit';
import { ProductDetail } from '../models/product-detail';
import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';

export interface ProductDetailOverviewReducerState {
    productDetail: ProductDetail | null;
    requesting: boolean;
    requestError?: ErrorData;
    isDirty: boolean;
    isValid: boolean;
    errors: FormikErrors<ProductDetailOverviewFormValues>;
    /**
     * The product as the server last handed it over (#2159). `productDetail`
     * cannot serve: leaving a tab parks its input right there.
     */
    baseline?: ProductDetail | null;
    /**
     * The article number the server refused because the supplier already has
     * one, and the supplier's name (#2117). Kept so the mask can say so at the
     * field instead of only reporting that saving failed.
     */
    takenNumber?: { number: string; supplier: string };
}

const initialProductDetailOverviewReducerState: ProductDetailOverviewReducerState = {
    productDetail: null,
    requesting: false,
    isDirty: false,
    isValid: true,
    errors: {},
};

/**
 * The refused article number out of a failed save, if that is what failed.
 *
 * The server answers a taken number with NON_UNIQUE_PROPERTIES and names the
 * field, the number and the supplier that holds it.
 */
const takenNumberOf = (errorData?: ErrorData): { number: string; supplier: string } | undefined => {
    if (ErrorMessage.NonUniqueProperties !== errorData?.title) {
        return undefined;
    }

    const context = errorData.context as { field?: string; number?: string; supplier?: string } | undefined;

    return 'number' === context?.field && context.number
        ? { number: context.number, supplier: context.supplier ?? '' }
        : undefined;
};

/**
 * Merges a localized text without inventing one.
 *
 * `{ ...undefined, ...undefined }` is `{}`, and an empty object where the
 * server has nothing is a difference: the mask reported unsaved input for the
 * rest of the session after one tab change, and the empty object would have
 * gone to the server on the next save (#2159).
 */
const mergeLocalized = (
    stored: LocalizedString | undefined,
    incoming: LocalizedString | undefined
): LocalizedString | undefined =>
    undefined === stored && undefined === incoming ? undefined : { ...stored, ...incoming };

export const productDetailOverviewReducer = (
    state: ProductDetailOverviewReducerState = initialProductDetailOverviewReducerState,
    action: AnyAction
): ProductDetailOverviewReducerState => {
    switch (action.type) {
        case PRODUCT_DETAIL_RESET: {
            return initialProductDetailOverviewReducerState;
        }

        case PRODUCT_DETAIL_OVERVIEW_FETCH: {
            return {
                ...state,
                requesting: true,
            };
        }

        case PRODUCT_DETAIL_OVERVIEW_RECEIVE: {
            return {
                ...state,
                productDetail: action['productDetail'],
                // The same values, kept apart from the ones the tabs edit.
                baseline: action['productDetail'],
                requesting: false,
                isDirty: false,
                // A verdict measured on the empty mask, before the article
                // arrived, said nothing about this article — it only put a
                // fault dot on a tab that was in order.
                isValid: true,
                errors: {},
                // It saved, so whatever number was taken no longer stands.
                takenNumber: undefined,
            };
        }

        case PRODUCT_DETAIL_OVERVIEW_ERROR: {
            return {
                ...state,
                requestError: action['errorData'],
                takenNumber: takenNumberOf(action['errorData']) ?? state.takenNumber,
                requesting: false,
            };
        }

        case PRODUCT_DETAIL_OVERVIEW_UPDATE: {
            const { formData, isDirty, isValid, errors } = action;

            // A mask that has not been filled from the server yet reports on an
            // empty form: every required field missing. That says nothing about
            // the article being loaded, and taken over it put a fault dot on a
            // tab that holds a complete one.
            if (!formData.number && !!state.productDetail?.number) {
                return state;
            }

            const nextDetail: ProductDetail = {
                ...state.productDetail,
                ...formData,
                shortDescription: mergeLocalized(state.productDetail?.shortDescription, formData.shortDescription),
                longDescription: mergeLocalized(state.productDetail?.longDescription, formData.longDescription),
                name: { ...state.productDetail?.name, ...formData.name },
                minimumOrderQuantity: formData.minimumOrderQuantity,
                maximumOrderQuantity: formData.maximumOrderQuantity,
                recommendedOrderQuantity: formData.recommendedOrderQuantity,
                quantityInterval: formData.quantityInterval,
                contentUnitId: formData.contentUnitId,
                contentQuantity: formData.contentQuantity,
            };

            // custom validation for quantity interval
            const customErrors = { ...errors };
            let customIsValid = isValid;
            const packingUnits = formData.packingUnits as PackingUnit[];
            packingUnits.forEach((packingUnit, index) => {
                if (!packingUnit.unitId) {
                    return;
                }
                if (nextDetail.quantityInterval && packingUnit.minimumQuantity % nextDetail.quantityInterval !== 0) {
                    lodashSet(customErrors, `packingUnits[${index}].minimumQuantity`, 'app.error.quantityInterval');
                    customIsValid = false;
                }
                if (nextDetail.minimumOrderQuantity && nextDetail.minimumOrderQuantity > packingUnit.minimumQuantity) {
                    lodashSet(customErrors, `packingUnits[${index}].minimumQuantity`, 'app.error.minimumOrderQuantity');
                    customIsValid = false;
                }
            });

            return {
                ...state,
                productDetail: nextDetail,
                isDirty: state.isDirty || isDirty,
                isValid: customIsValid,
                errors: customErrors,
            };
        }

        case PRODUCT_DETAIL_ACTIVATE: {
            return {
                ...state,
                productDetail: state.productDetail && {
                    ...state.productDetail,
                    isActive: true,
                },
                // Already on the server; without this the mask would call
                // itself unsaved for the rest of the session.
                baseline: state.baseline && { ...state.baseline, isActive: true },
            };
        }

        case PRODUCT_DETAIL_DEACTIVATE: {
            return {
                ...state,
                productDetail: state.productDetail && {
                    ...state.productDetail,
                    isActive: false,
                },
                // Already on the server; without this the mask would call
                // itself unsaved for the rest of the session.
                baseline: state.baseline && { ...state.baseline, isActive: false },
            };
        }

        default: {
            return state;
        }
    }
};
